import { useEffect, useState } from "react";
import { Package, Eye, MessageSquare, Check, Truck, X, Clock, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { getOrders, updateOrder, getOrderStats } from "@/lib/api";
import { formatDateTime, formatPrice } from "@/lib/dateUtils";

const formatDate = (dateStr) => formatDateTime(dateStr);

const statusColors = {
  "In Behandeling": "bg-yellow-500/20 text-yellow-600 border-yellow-500/30",
  "Bevestigd": "bg-blue-500/20 text-blue-600 border-blue-500/30",
  "In Verwerking": "bg-purple-500/20 text-purple-600 border-purple-500/30",
  "Verzonden": "bg-cyan-500/20 text-cyan-600 border-cyan-500/30",
  "Afgeleverd": "bg-green-500/20 text-green-600 border-green-500/30",
  "Geannuleerd": "bg-red-500/20 text-red-600 border-red-500/30",
};

const statusIcons = {
  "In Behandeling": Clock,
  "Bevestigd": Check,
  "In Verwerking": Package,
  "Verzonden": Truck,
  "Afgeleverd": Check,
  "Geannuleerd": X,
};

// Status mapping van filter codes naar database waarden
const statusMapping = {
  "pending": "In Behandeling",
  "confirmed": "Bevestigd",
  "processing": "In Verwerking",
  "shipped": "Verzonden",
  "delivered": "Afgeleverd",
  "cancelled": "Geannuleerd",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [respondDialogOpen, setRespondDialogOpen] = useState(false);
  const [responseForm, setResponseForm] = useState({
    status: "",
    admin_notes: "",
    admin_response: ""
  });

  const fetchOrders = async () => {
    try {
      // Map filter code to database status value
      const status = statusFilter !== "all" ? statusMapping[statusFilter] : undefined;
      const params = status ? { status } : {};
      const [ordersRes, statsRes] = await Promise.all([
        getOrders(params),
        getOrderStats()
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Fout bij het ophalen van bestellingen:", error);
      toast.error("Kon bestellingen niet laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setViewDialogOpen(true);
  };

  const handleRespondOrder = (order) => {
    setSelectedOrder(order);
    setResponseForm({
      status: order.status,
      admin_notes: order.admin_notes || "",
      admin_response: order.admin_response || ""
    });
    setRespondDialogOpen(true);
  };

  const handleSaveResponse = async () => {
    try {
      await updateOrder(selectedOrder.id, responseForm);
      toast.success("Bestelling bijgewerkt");
      setRespondDialogOpen(false);
      fetchOrders();
    } catch (error) {
      console.error("Fout bij het bijwerken van bestelling:", error);
      toast.error("Kon bestelling niet bijwerken");
    }
  };

  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.order_number.toLowerCase().includes(query) ||
      order.customer_name.toLowerCase().includes(query) ||
      order.customer_email.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
          <div className="h-96 bg-slate-200 dark:bg-slate-700 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-50 rounded-xl">
            <Package className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 font-['Chivo'] tracking-tight">Bestellingen</h1>
            <p className="text-slate-500 font-['Manrope'] mt-0.5">Beheer klantbestellingen</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 font-['Manrope']">Totaal Bestellingen</p>
              <p className="text-2xl font-black text-slate-900 font-['Chivo']">{stats.total_orders}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 font-['Manrope']">Totale Omzet</p>
              <p className="text-2xl font-black text-emerald-600 font-['Chivo']">{formatPrice(stats.total_revenue)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 font-['Manrope']">Totale Kosten</p>
              <p className="text-2xl font-black text-orange-600 font-['Chivo']">{formatPrice(stats.total_cost || 0)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 font-['Manrope']">Totale Winst</p>
              <p className={`text-2xl font-black font-['Chivo'] ${(stats.total_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatPrice(stats.total_profit || 0)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 font-['Manrope']">In Behandeling</p>
              <p className="text-2xl font-black text-amber-600 font-['Chivo']">{stats.by_status?.["In Behandeling"]?.count || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 font-['Manrope']">Afgeleverd</p>
              <p className="text-2xl font-black text-blue-600 font-['Chivo']">{stats.by_status?.["Afgeleverd"]?.count || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Zoek op bestelnummer, naam of email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white border-slate-200 font-['Manrope']"
            data-testid="search-orders"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-white border-slate-200 font-['Manrope']" data-testid="status-filter">
            <SelectValue placeholder="Filter op status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Statussen</SelectItem>
            <SelectItem value="pending">In Behandeling</SelectItem>
            <SelectItem value="confirmed">Bevestigd</SelectItem>
            <SelectItem value="processing">In Verwerking</SelectItem>
            <SelectItem value="shipped">Verzonden</SelectItem>
            <SelectItem value="delivered">Afgeleverd</SelectItem>
            <SelectItem value="cancelled">Geannuleerd</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 dark:border-slate-700">
                <TableHead className="text-slate-600 dark:text-slate-400">Bestel #</TableHead>
                <TableHead className="text-slate-600 dark:text-slate-400">Klant</TableHead>
                <TableHead className="text-slate-600 dark:text-slate-400">Stukken</TableHead>
                <TableHead className="text-slate-600 dark:text-slate-400">Totaal</TableHead>
                <TableHead className="text-slate-600 dark:text-slate-400">Status</TableHead>
                <TableHead className="text-slate-600 dark:text-slate-400">Datum</TableHead>
                <TableHead className="text-slate-600 dark:text-slate-400">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => {
                  const StatusIcon = statusIcons[order.status] || Clock;
                  return (
                    <TableRow 
                      key={order.id} 
                      className="border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                      data-testid={`order-row-${order.id}`}
                    >
                      <TableCell className="font-mono text-sm text-slate-900 dark:text-white">
                        {order.order_number}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{order.customer_name}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{order.customer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-300">
                        {order.items.length} Stuk(ken)
                      </TableCell>
                      <TableCell className="font-medium text-slate-900 dark:text-white">
                        {formatPrice(order.total)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusColors[order.status]} flex items-center gap-1 w-fit`}>
                          <StatusIcon className="h-3 w-3" />
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400 text-sm">
                        {formatDate(order.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleViewOrder(order)}
                            className="border-slate-200 dark:border-slate-600"
                            data-testid={`view-order-${order.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleRespondOrder(order)}
                            className="border-slate-200 dark:border-slate-600"
                            data-testid={`respond-order-${order.id}`}
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500 dark:text-slate-400">
                    Geen bestellingen gevonden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">
              Bestelling Details - {selectedOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white mb-2">Klantgegevens</h4>
                  <p className="text-slate-600 dark:text-slate-400">{selectedOrder.customer_name}</p>
                  <p className="text-slate-600 dark:text-slate-400">{selectedOrder.customer_email}</p>
                  <p className="text-slate-600 dark:text-slate-400">{selectedOrder.customer_phone}</p>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white mb-2">Verzendadres</h4>
                  <p className="text-slate-600 dark:text-slate-400">{selectedOrder.shipping_address}</p>
                  <p className="text-slate-600 dark:text-slate-400">
                    {selectedOrder.shipping_postal} {selectedOrder.shipping_city}
                  </p>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-2">Bestelde Artikelen</h4>
                <div className="space-y-2">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.product_name} className="w-12 h-12 object-cover rounded" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 dark:text-white">{item.product_name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Aantal: {item.quantity}</p>
                      </div>
                      <p className="font-medium text-slate-900 dark:text-white">
                        {formatPrice(item.price * item.quantity)}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-4 pt-4 border-t border-slate-200 dark:border-slate-600">
                  <span className="font-medium text-slate-900 dark:text-white">Totaal</span>
                  <span className="text-xl font-bold text-blue-600">{formatPrice(selectedOrder.total)}</span>
                </div>
              </div>

              {/* Admin Response */}
              {selectedOrder.admin_response && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Admin Reactie</h4>
                  <p className="text-blue-800 dark:text-blue-200">{selectedOrder.admin_response}</p>
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                    Door {selectedOrder.responded_by} op {formatDate(selectedOrder.responded_at)}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Respond to Order Dialog */}
      <Dialog open={respondDialogOpen} onOpenChange={setRespondDialogOpen}>
        <DialogContent className="max-w-lg bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">
              Reageren op Bestelling - {selectedOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
              <strong>Let op:</strong> Bij het opslaan wordt automatisch een email verstuurd naar de klant 
              wanneer de status wijzigt of een reactie wordt toegevoegd.
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Status Bijwerken</Label>
              <Select 
                value={responseForm.status} 
                onValueChange={(value) => setResponseForm({...responseForm, status: value})}
              >
                <SelectTrigger className="bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="In Behandeling">In Behandeling</SelectItem>
                  <SelectItem value="Bevestigd">Bevestigd</SelectItem>
                  <SelectItem value="In Verwerking">In Verwerking</SelectItem>
                  <SelectItem value="Verzonden">Verzonden</SelectItem>
                  <SelectItem value="Afgeleverd">Afgeleverd</SelectItem>
                  <SelectItem value="Geannuleerd">Geannuleerd</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Interne Notities (niet zichtbaar voor klant)</Label>
              <Textarea
                value={responseForm.admin_notes}
                onChange={(e) => setResponseForm({...responseForm, admin_notes: e.target.value})}
                placeholder="Voeg interne notities toe..."
                className="bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Reactie naar Klant (wordt per email verzonden)</Label>
              <Textarea
                value={responseForm.admin_response}
                onChange={(e) => setResponseForm({...responseForm, admin_response: e.target.value})}
                placeholder="Schrijf een reactie naar de klant..."
                className="bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRespondDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSaveResponse} className="bg-blue-600 hover:bg-blue-700">
              Opslaan & Email Versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
